'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Alert01Icon,
  Cancel01Icon,
  Tick02Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getChannelIcon } from '@/lib/budget/channel-icons'
import type { SubchannelView } from '@/app/(lobby)/settings/page'

type AuthMode = 'atrium_managed' | 'user_oauth'
type PillStatus = 'enabled' | 'disabled' | 'error'

export interface SettingsConnectionPanelProps {
  subchannel: SubchannelView | null
  channelAccentHex: string
  channelName: string
  onClose: () => void
  onChanged: () => void
}

export function pillStatusFor(sub: SubchannelView): PillStatus {
  const status = sub.connection?.status
  if (status === 'error') return 'error'
  if (status === 'connected' || status === 'pending') return 'enabled'
  return 'disabled'
}

export function StatusPill({ status }: { status: PillStatus }) {
  if (status === 'enabled') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-sherman-line bg-sherman-paper px-2.5 py-1 text-[11px] font-medium text-emerald-600">
        <HugeiconsIcon icon={Tick02Icon} className="size-3" />
        Enabled
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-sherman-line bg-sherman-paper px-2.5 py-1 text-[11px] font-medium text-red-600">
        <HugeiconsIcon icon={Alert01Icon} className="size-3" />
        Error
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full border border-sherman-line bg-sherman-paper px-2.5 py-1 text-[11px] font-medium text-sherman-muted">
      Disabled
    </span>
  )
}

export function SettingsConnectionPanel({
  subchannel,
  channelAccentHex,
  channelName,
  onClose,
  onChanged,
}: SettingsConnectionPanelProps) {
  const [busy, setBusy] = useState(false)
  const [pendingMode, setPendingMode] = useState<AuthMode>(
    pickDefaultAuthMode(subchannel?.default_auth_mode ?? 'either')
  )

  useEffect(() => {
    if (subchannel) {
      setPendingMode(pickDefaultAuthMode(subchannel.default_auth_mode))
      setBusy(false)
    }
  }, [subchannel])

  const connected = isConnected(subchannel)
  const connectionStatus = subchannel?.connection?.status
  const authMode = subchannel?.connection?.auth_mode ?? pendingMode
  const modeOptions = subchannel ? authModeOptionsFor(subchannel) : []
  const ChannelIcon = subchannel ? getChannelIcon(subchannel.channel_key) : null
  const accountLabel = subchannel ? accountLabelFor(subchannel) : ''
  const detailLine = subchannel ? connectionDetailFor(subchannel) : null

  const handleEnable = async (mode: AuthMode = pendingMode) => {
    if (busy || !subchannel) return
    setBusy(true)
    try {
      const res = await fetch('/api/connectors/connections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subchannel_key: subchannel.key, auth_mode: mode }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`)
      if (data?.redirect_url) {
        window.location.href = data.redirect_url
        return
      }
      toast.success(`${subchannel.name} connected`)
      onChanged()
      onClose()
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const handleDisable = async () => {
    if (busy || !subchannel?.connection?.id) return
    setBusy(true)
    try {
      const res = await fetch(`/api/connectors/connections/${subchannel.connection.id}`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || `HTTP ${res.status}`)
      }
      toast.success(`${subchannel.name} disconnected`)
      onChanged()
      onClose()
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOutPanel
      open={subchannel != null}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      {subchannel && (
        <div className="flex min-h-full flex-col">
          <div className="border-b border-sherman-line bg-sherman-paper px-6 py-5">
            <div className="flex items-start gap-4">
              <div
                className="grid size-10 shrink-0 place-items-center rounded-lg font-mono text-[13px] font-semibold text-white"
                style={{ background: channelAccentHex }}
              >
                {ChannelIcon ? (
                  <HugeiconsIcon icon={ChannelIcon} className="size-5" />
                ) : (
                  <span>{subchannel.logo_text || subchannel.name[0]}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-[16px] font-semibold text-sherman-ink">
                    {subchannel.name}
                  </h2>
                  <StatusPill status={pillStatusFor(subchannel)} />
                </div>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-sherman-muted">
                  {channelName}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="grid size-8 shrink-0 place-items-center rounded-full text-sherman-muted transition-colors hover:bg-black/[0.04] hover:text-sherman-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-sherman-accent/40"
                aria-label="Close connection settings"
              >
                <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-6 px-6 py-6">
            {!connected && (
              <div>
                <label className="mb-2 block text-[12px] font-medium text-sherman-muted">
                  Account type
                </label>
                <Select
                  value={pendingMode}
                  onValueChange={value => setPendingMode(value as AuthMode)}
                  disabled={busy}
                >
                  <SelectTrigger className="w-full" aria-label="Connection mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {modeOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {connected && (
              <div className="rounded-lg border border-sherman-line bg-white px-4 py-4">
                <p className="text-[12px] font-medium text-sherman-muted">Account</p>
                <p className="mt-1 text-[14px] font-medium text-sherman-ink">{accountLabel}</p>
                {detailLine && (
                  <p className="mt-1 text-[12px] text-sherman-muted">{detailLine}</p>
                )}
              </div>
            )}

            {connectionStatus === 'error' && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-medium text-red-700">
                Connection error. Reconnect to fix.
              </div>
            )}

            <div className="mt-auto space-y-3 pt-2">
              {!connected && (
                <button
                  type="button"
                  onClick={() => handleEnable(pendingMode)}
                  disabled={busy}
                  className="flex h-11 w-full items-center justify-center rounded-lg px-4 text-[14px] font-medium text-white transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-60 focus:outline-none focus-visible:outline-2 focus-visible:outline-sherman-accent/40"
                  style={{ background: channelAccentHex }}
                >
                  {busy ? 'Enabling...' : 'Enable'}
                </button>
              )}

              {connected && connectionStatus === 'error' && (
                <button
                  type="button"
                  onClick={() => handleEnable(authMode)}
                  disabled={busy}
                  className="flex h-11 w-full items-center justify-center rounded-lg px-4 text-[14px] font-medium text-white transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-60 focus:outline-none focus-visible:outline-2 focus-visible:outline-sherman-accent/40"
                  style={{ background: channelAccentHex }}
                >
                  {busy ? 'Reconnecting...' : 'Reconnect'}
                </button>
              )}

              {connected && (
                <button
                  type="button"
                  onClick={handleDisable}
                  disabled={busy}
                  className="flex h-11 w-full items-center justify-center rounded-lg border border-red-200 bg-white px-4 text-[14px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-60 focus:outline-none focus-visible:outline-2 focus-visible:outline-red-300"
                >
                  {busy ? 'Disabling...' : 'Disable'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </SlideOutPanel>
  )
}

function pickDefaultAuthMode(mode: 'atrium_managed' | 'user_oauth' | 'either'): AuthMode {
  if (mode === 'atrium_managed') return 'atrium_managed'
  if (mode === 'user_oauth') return 'user_oauth'
  return 'atrium_managed'
}

function isConnected(subchannel: SubchannelView | null): boolean {
  const status = subchannel?.connection?.status
  return status === 'connected' || status === 'pending' || status === 'error'
}

function authModeOptionsFor(subchannel: SubchannelView) {
  const options: Array<{ value: AuthMode; label: string }> = []
  if (
    subchannel.default_auth_mode === 'atrium_managed' ||
    subchannel.default_auth_mode === 'either'
  ) {
    options.push({ value: 'atrium_managed', label: 'Atrium managed' })
  }
  if (subchannel.default_auth_mode === 'user_oauth' || subchannel.default_auth_mode === 'either') {
    options.push({ value: 'user_oauth', label: `Your ${subchannel.name}` })
  }
  return options
}

function accountLabelFor(subchannel: SubchannelView): string {
  const conn = subchannel.connection
  if (!conn) return ''
  return (
    conn.display_account_name ||
    (conn.auth_mode === 'atrium_managed' ? 'Managed by Atrium' : subchannel.name)
  )
}

function connectionDetailFor(subchannel: SubchannelView): string | null {
  const conn = subchannel.connection
  if (!conn) return null

  const connectedAt = formatTimeAgo(conn.connected_at)
  const syncedAt = formatTimeAgo(conn.last_synced_at)
  if (connectedAt && syncedAt) return `Connected ${connectedAt} · synced ${syncedAt}`
  if (connectedAt) return `Connected ${connectedAt}`
  if (syncedAt) return `Synced ${syncedAt}`
  return null
}

function formatTimeAgo(iso: string | null): string | null {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return 'just now'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks}w ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}
